
import { AuthenticationDetails, CognitoAccessToken, CognitoRefreshToken, CognitoUser as AwsCognitoUser, CognitoUserAttribute, CognitoUserPool, CognitoUserSession, ICognitoUserAttributeData } from 'amazon-cognito-identity-js'
import { HttpRequest } from '../../../util/http';
import { CognitoNewPasswordRequiredError } from './CognitoErrors';
import { useState, useEffect } from 'react'
import { error } from 'highcharts';

export interface ICognitoUserSerialized {
  Username: string;
  AvannisUsername: string;
  Token: string;
  RefreshToken: string;
  AccessToken: string;
}

export interface IGetCognitoUsernameResponse {
  status: number;
  CognitoUsername: string;
  WasCreated: boolean;
  Email: string;
  reason?: string;
}

export interface IGetLoginResults {
  code: string;
  name: string;
  message: string;
}

export interface IAvannisLoginResponse {
  default: string
  redirect?: string
  status: number
}

export class CognitoUser implements ICognitoUserSerialized {
  /**
   * Is the error of CognitoNewPasswordRequiredError type
   * @param err
   */
  public static isNewPasswordRequiredError(err: any) {
    return err instanceof CognitoNewPasswordRequiredError
  }

  /**
   * Removes saved user from Local Storage
   */
  public static removeLocalUserFromStorage() {
    localStorage.removeItem(CognitoUser.LOCAL_STORAGE_KEY)
  }

  /**
   * Load user from local storage and re-authorize them for usage.
   * If authorization fails, user is logged out
   */
  public static async GetLoggedInUser(bounceUserIfNotAuthorized = false) {
    try {
      const userJSONStr = localStorage.getItem(CognitoUser.LOCAL_STORAGE_KEY)
      const user = userJSONStr ? JSON.parse(userJSONStr) as ICognitoUserSerialized : null
      const duckPunchWorks = user !== null && typeof user.RefreshToken === 'string' && typeof user.Token === 'string' && typeof user.Username === 'string'

      if (user !== null && duckPunchWorks) {
        const cognitoUser = new CognitoUser(user.AvannisUsername)
        cognitoUser.init(user.Username)

        await new Promise((resolve, reject) => {
          cognitoUser.awsCognitoUser.getSession((err: any, session?: CognitoUserSession) => {
            if (err || !session) {
              reject('Unable to get session!')
            }

            if (!session.isValid()) {
              reject('Session is not valid!')
            }

            // @ts-ignore
            resolve()
          })
        })

        // tslint:disable-next-line:no-console
        //console.log('User was reauthorized!')

        return cognitoUser
      }
    } catch (err) {
      CognitoUser.removeLocalUserFromStorage()

      if (bounceUserIfNotAuthorized) {
        // tslint:disable-next-line:no-console
        console.error(`Unable to authorize user. Sending them to login page. Error: ${err}`)
        const currentPathname = location.pathname
        const indexPathname = '/index'
        const currentPathIsIndex = currentPathname.indexOf(indexPathname) !== 0

        if (!currentPathIsIndex) {
          location.href = indexPathname + '?redirect=' + encodeURIComponent(currentPathname);
        }
      }
    }

    return Promise.reject()
  }

  private static LOCAL_STORAGE_KEY = 'U$3R'
  private static GroupName = 'AuthenticatedUsers'

  private static UserPoolId = process.env.COGNITO_POOL_ID
  private static ClientId = process.env.COGNITO_CLIENT_ID

  public Token: string
  public Username: string
  public AvannisUsername: string
  public RefreshToken: string
  public AccessToken: string

  private awsCognitoUser!: AwsCognitoUser;
  private awsCognitoUserPool!: CognitoUserPool;

  constructor(AvannisUsername: string) {
    this.AvannisUsername = AvannisUsername;
  }


  /**
   * @param Username
   * @param Password
   */
  public login(Password: string) {
    return new Promise<void>((resolve, reject) => {

      const cognitoUserNamePromise = this.getCognitoUsername();

      //NOTE: Invalid username
      cognitoUserNamePromise.catch((err) => reject(err));

      cognitoUserNamePromise.then((cognitoRes) => {
        if (cognitoRes.reason === 'ip_restricted') {
          const err = { message: 'Your institution restricts access by IP address. Please verify that your device is using an IP address authorized by your institution.' }
          this.log_login_results(err)
          reject(err)
          return
        }
        const Username = cognitoRes.CognitoUsername
        let preAuthPromise = Promise.resolve(undefined)

        //re-init with cognito username
        this.init(Username)

        if (cognitoRes.WasCreated) {
          preAuthPromise = this.signUp(this.AvannisUsername, cognitoRes.Email, Password)
            //Remove mapping if signup fails
            .catch((err) => {
              this.deleteCognitoUserMapping(Username);
              this.log_login_results(err)
              reject(err)
            })
        }

        preAuthPromise.then(() => {
          const authenticationDetails = new AuthenticationDetails({ Username, Password })

          this.awsCognitoUser.authenticateUser(authenticationDetails, {
            onFailure: (err) => {
              if ('message' in err && cognitoRes.reason !== 'ip_restricted') {
                err.message = 'Your credentials are incorrect.  Please enter the correct credentials or click the "Forgot Your Password?" link below to continue.';
                this.log_login_results(err);
              }
              reject(err)
            },
            newPasswordRequired: (userAttributes: any, requiredAttributes: any) => {
              const newPassError = new CognitoNewPasswordRequiredError('Cognito: New Password Required')
              this.log_login_results(newPassError);
              reject(newPassError)
            },
            onSuccess: (res) => {
              // AVR-186 (Door 2): the password was just validated against the old pool, so
              // this is the one moment we hold it in the clear. Best-effort hand it — plus the
              // just-issued legacy-pool id token — to the server shim so reports.api can verify
              // the caller and provision the user into the NEW pool for a future direct login.
              // Fire-and-forget — never awaited, errors swallowed — so it can never delay or
              // break the legacy login below.
              this.migrateToNewPool(Password, res.getIdToken().getJwtToken())
              // I need to get a hold of the info in this res. I need it in the function below
              this.avannisLogin(res)
                .then(() => {
                  this.log_login_results(res)
                  this.applyAndSaveSession(res, resolve)
                })
                .catch((err) => {
                  this.log_login_results(err)
                  reject(err)
                })
            }
          })
        })
      })

    })
  }


  /**
   * @param Username
   * @param Password
   */
  public authUser(Password: string) {
    return new Promise<void>((resolve, reject) => {
      const cognitoUserNamePromise = this.getCognitoUsername();

      //NOTE: Invalid username
      cognitoUserNamePromise.catch((err) => reject(err));

      cognitoUserNamePromise.then((cognitoRes) => {
        if (cognitoRes.reason === 'ip_restricted') {
          const err = { message: 'Your institution restricts access by IP address. Please verify that your device is using an IP address authorized by your institution.' }
          reject(err)
          return
        }
        const Username = cognitoRes.CognitoUsername
        let preAuthPromise = Promise.resolve(undefined)

        //re-init with cognito username
        this.init(Username)

        if (cognitoRes.WasCreated) {
          preAuthPromise = this.signUp(this.AvannisUsername, cognitoRes.Email, Password)
            //Remove mapping if signup fails
            .catch((err) => {
              this.deleteCognitoUserMapping(Username);
              reject(err)
            })
        }

        preAuthPromise.then(() => {
          const authenticationDetails = new AuthenticationDetails({ Username, Password })

          this.awsCognitoUser.authenticateUser(authenticationDetails, {
            onFailure: (err) => {
              if ('message' in err)
                err.message = 'Password is incorrect.'
              reject(err)
            },
            newPasswordRequired: (userAttributes: any, requiredAttributes: any) => {
              const newPassError = new CognitoNewPasswordRequiredError('Cognito: New Password Required')

              reject(newPassError)
            },
            onSuccess: (res) => {
              console.log('SUCCESSFUL AUTH')
              // this.avannisLogin()
              //   .then(() => this.applyAndSaveSession(res, resolve))
              //   .catch((err) => reject(err))
            }
          })
        })
      })
    })
  }

  /**
   * Update a user's email attribute in Cognito
   * @param email
   */
  public updateEmail(email: string) {
    const dataEmail: ICognitoUserAttributeData = { Name: 'email', Value: email };
    const attributes = [dataEmail];

    return new Promise((resolve, reject) => {
      this.awsCognitoUser.updateAttributes(attributes, (err, result) => {
        if (err) {
          reject(err)
          return
        }

        // @ts-ignore
        resolve()
      })
    })
  }

  /**
   * Refresh Authorization with RefreshToken from AWS
   * @param RefreshToken
   */
  public refreshAuth(RefreshToken: string) {
    const refreshToken = new CognitoRefreshToken({ RefreshToken })

    return new Promise<CognitoUser>((resolve, reject) => {

      this.awsCognitoUser.refreshSession(refreshToken, (err, session?: CognitoUserSession) => {
        if (session) {
          this.applyAndSaveSession(session)

          return resolve(this)
        }

        reject()
      })
    })
  }

  /**
   * Create a new password for a user
   * Warning: a login() must be attempted first
   * @param newPassword
   */
  public completeNewPasswordChallenge(newPassword: string) {
    const attributesData = {}

    return new Promise<void>((resolve, reject) => {
      this.awsCognitoUser.completeNewPasswordChallenge(newPassword, attributesData, {
        onSuccess: (res) => this.applyAndSaveSession(res, resolve),
        onFailure: (err) => reject(err),
      })
    })
  }

  /**
   * Updates Cognito User's password
   */
  public updatePassword(confirmationCode: any, newPassword: string, username: string) {
    return new Promise((resolve, reject) => {
      this.getCognitoUsernameWithoutCreate().then((resp) => {
        this.init(resp.CognitoUsername)

        this.awsCognitoUser.confirmPassword(confirmationCode, newPassword, {
          onSuccess: () => {
            // @ts-ignore
            resolve()
          },
          onFailure: (err) => {
            reject(err)
          }
        })
      }).catch((err) => {
        reject(err)
      })
    })
  }

  /**
   * Updates Cognito User's password
   */
  public updatePasswordWithoutConfirmationCode(oldPassword: string, newPassword: string, username: string) {
    return new Promise((resolve, reject) => {
      this.getCognitoUsernameWithoutCreate().then((resp) => {
        this.init(resp.CognitoUsername)
        this.awsCognitoUser.getSession((session) => {
          this.awsCognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
            console.log(result)
          })
        })
      }).catch((err) => {
        reject(err)
      })
    })
  }

  public signUpUser(username: string, email: string, password: string, cognitoUsername: string) {
    const dataEmail = new CognitoUserAttribute({ Name: 'email', Value: email })
    const dataAvannisUsername = new CognitoUserAttribute({ Name: 'custom:avannis_username', Value: username })
    const attributeList = [dataEmail, dataAvannisUsername]
    return new Promise((resolve, reject) => {
      this.init(username)
      this.awsCognitoUserPool.signUp(cognitoUsername, password, attributeList, null, (err, res) => {
        if (err || !res.user) {
          reject(err)
        }

        this.awsCognitoUser = res.user

        // @ts-ignore
        resolve()
      })
    })
  }

  public forgotPassword(username: string) {
    return new Promise((resolve, reject) => {
      this.getCognitoUsernameWithoutCreate().then((resp) => {
        this.init(resp.CognitoUsername)
        this.awsCognitoUser.forgotPassword({
          onSuccess: (result) => {
            resolve(result)
          },
          onFailure: (err) => { reject(err) }
        }
        )
      }).catch((err: Error) => {
        err.message = 'Could not find this Avannis username. We have recently upgraded our security. If you continue to have a problem, please send an email to techsupport@avannis.com for assistance.'
        reject(err)
      })
    })
  }

  /**
   * Serialize User for storage
   */
  public serialize(): ICognitoUserSerialized {
    // tslint:disable-next-line:no-this-assignment
    const { RefreshToken, Token, Username, AccessToken, AvannisUsername } = this

    return { RefreshToken, Token, Username, AccessToken, AvannisUsername };
  }

  /**
   * ALMOST NEVER USE!!!
   * Deletes mapping between avannis' username and cognito's
   * @param CognitoUsername
   */
  private deleteCognitoUserMapping(CognitoUsername: string) {
    const method = 'POST'
    const data = { CognitoUsername }

    return HttpRequest.fetch('/scripts/auth/DeleteCognitoUsername', { method, data })
  }

  /**
   * Before a user signs up, make sure they are who they say they are.
   * @param AvannisUsername 
   * @param Password 
   */
  private verifyUserBeforeSignup(AvannisUsername: string, Password: string) {
    const method = 'POST'
    const data = { AvannisUsername, Password }

    return HttpRequest.fetch('/scripts/auth/VerifyAvannisUser', { method, data })
  }

  /**
   * Signup new Cognito user
   */
  private signUp(AvannisUsername: string, Email: string, Password: string) {
    const dataEmail = new CognitoUserAttribute({ Name: 'email', Value: Email })
    const dataAvannisUsername = new CognitoUserAttribute({ Name: 'custom:avannis_username', Value: AvannisUsername })
    const attributeList = [dataEmail, dataAvannisUsername]

    return new Promise((resolve, reject) => {

      this.verifyUserBeforeSignup(AvannisUsername, Password).then(() => {
        this.awsCognitoUserPool.signUp(this.Username, Password, attributeList, null, (err, res) => {
          if (err || !res.user) {
            reject(err)
          }

          this.awsCognitoUser = res.user

          // @ts-ignore
          resolve()
        })
      }).catch((err: Error) => {
        err.message = 'Could not verify username and password.   Please enter the correct credentials or click the "Forgot Your Password?" link below to continue.'
        reject(err)
      })
    })
  }

  /**
   * ONLY USE ONCE COGNITO HAS AUTHORIZED!
   */
  private async avannisLogin(redirect) {
    const token = redirect.getAccessToken().getJwtToken()
    // const token = this.Username;
    // const username = this.AvannisUsername
    const data = { token };
    const method = 'POST';
    const queryParamStart = location.href.indexOf('?')
    let endpoint = '/scripts/login';
    let queryParams = ''

    //Add redirect params
    if (queryParamStart >= 0) {
      queryParams = location.href.substr(queryParamStart)
      endpoint += queryParams
    }

    return HttpRequest.fetch<IAvannisLoginResponse>(endpoint, { method, data }).then((res) => {
      this.log_login_results(res).then(response => {
        if (redirect) {
          const newHref = res.redirect ? res.redirect : res.default
          location.href = newHref
        } else {
          return res
        }
      }).catch(err => {
        return err
      })
    })
  }

  /**
   * Init object instance
   * @param Username
   */
  private init(Username: string) {
    const { UserPoolId, ClientId, GroupName } = CognitoUser
    this.awsCognitoUserPool = new CognitoUserPool({ UserPoolId, ClientId })
    const Pool = this.awsCognitoUserPool;
    const userData = { Username, Pool, GroupName } //is GroupName necessary?

    this.Username = Username
    this.awsCognitoUser = new AwsCognitoUser(userData)
  }


  /**
   * Given a user's Avannis username, get their Cognito Username
   */
  private getCognitoUsername() {
    const Username = this.AvannisUsername
    const data = { Username };
    const method = 'POST';
    const endpoint = '/scripts/auth/GetCognitoUsername';

    return HttpRequest.fetch<IGetCognitoUsernameResponse>(endpoint, { method, data })
  }

  /**
   * AVR-186 (Door 2): best-effort migration of the legacy user into the new Cognito
   * pool. POSTs the just-validated credentials plus the just-issued legacy-pool id token
   * to a same-origin legacy PHP shim, which keeps the X-API-Key server-side and forwards
   * them to reports.api. The id token lets both the shim and reports.api verify the caller
   * really authenticated (reports.api provisions the token's verified email, not any
   * caller-supplied field). Must never block or break legacy login: not awaited by the
   * caller, and any failure is swallowed. The server shim is fail-safe OFF when
   * unconfigured, so this call is a harmless no-op until the feature is enabled.
   */
  private migrateToNewPool(Password: string, IdToken: string) {
    const data = { Username: this.AvannisUsername, Password, IdToken };
    const method = 'POST';
    const endpoint = '/scripts/auth/MigrateLegacyUser';

    // keepalive lets the request outlive the login redirect (avannisLogin sets location.href
    // ~sub-second later, while the shim round-trip is budgeted at 5-17s). Without it the browser
    // cancels the in-flight POST on navigation. Body here is tiny (username + password + a JWT),
    // far under keepalive's 64KB cap — the navigator.sendBeacon use case.
    return HttpRequest.fetch(endpoint, { method, data, keepalive: true }).catch(() => {
      // Swallow — migration is best-effort and must not affect the user's login.
    });
  }

  private getCognitoUsernameWithoutCreate() {
    const Username = this.AvannisUsername
    const data = { Username };
    const method = 'POST';
    const endpoint = '/scripts/auth/GetCognitoUsernameWithoutCreate';

    return HttpRequest.fetch<IGetCognitoUsernameResponse>(endpoint, { method, data })
  }

  /*private async getIPInfo() {
    return fetch('https://ipapi.co/json/')
      .then((response) => response.json())
      .then((responseJson) => {
        return responseJson;
      })
      .catch(ex => {
        //return [ex];
        return Promise.reject(ex);
      });
  }*/

  private async getIPInfo() {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const ipInfo = await response.json();
      return ipInfo;
    } catch (error) {
      console.error("Error fetching IP info:", error);
      return { "ip": "not found" };
    }
  }

  private async log_login_results(res) {
    const Username = this.AvannisUsername;
    let ipinfo = await this.getIPInfo();
    /*
    let ipinfo = this.getIPInfo().then((ipInfo) => {
      // Handle successful response
      return (ipInfo);
    })
      .catch((error) => {
        // Handle error
        return ([{ "ip": "not found" }]);
      });
    */
    const serverHostname = window.location.protocol + '//' + window.location.hostname;

    let loginResult = 'Successful login';
    if ('code' in res) {
      loginResult = res['code'];
    }

    var formdata = new FormData();
    formdata.append("Username", Username);
    formdata.append("ip_info", JSON.stringify(ipinfo));
    formdata.append("results", JSON.stringify(res));
    formdata.append("login_results", loginResult);

    var requestOptions = {
      method: 'POST',
      body: formdata
    };

    try {
      const response = await fetch(`${serverHostname}/scripts/auth/log_login_results`, requestOptions);
      console.log(loginResult);
      return response;
    } catch (error) {
      console.log('error', error);
    }

  }





  /**
   * Apply and Save a session
   * @param result
   */
  private applyAndSaveSession(result: CognitoUserSession, callback?: () => void) {
    const Token = result.getIdToken().getJwtToken()
    const RefreshToken = result.getRefreshToken().getToken();
    const AccessToken = result.getAccessToken().getJwtToken();

    //HttpRequest.updateAuthToken(Token)

    this.RefreshToken = RefreshToken
    this.Token = Token
    this.AccessToken = AccessToken

    //Perform after tokens are set
    const userSerialized = this.serialize()
    this.saveUserToLocalStorage(userSerialized)


    if (callback) {
      callback()
    }
  }

  /**
   * Save current user to local storage
   */
  private saveUserToLocalStorage(userSerialized: ICognitoUserSerialized) {
    try {
      const userStr = JSON.stringify(userSerialized)
      localStorage.setItem(CognitoUser.LOCAL_STORAGE_KEY, userStr)
    } catch (e) {
      // tslint:disable-next-line:no-console
      console.error('Unable to save user credentials')
    }
  }
}
